/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HistoryIcon from "@mui/icons-material/History";
import LabelIcon from "@mui/icons-material/Label";
import ShortTextIcon from "@mui/icons-material/ShortText";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useIntl } from "react-intl";
import stopTypes from "../../../../models/stopTypes";
import weightTypes from "../../../../models/weightTypes";
import ModalityIconImg from "../../../MainPage/ModalityIconImg";
import { StopPlaceMembership, TagTray } from "../../Shared";
import {
  getModalityAllowance,
  isModalityAllowed,
  isStopPlaceTypeValid,
  useHiddenStopTypes,
} from "../../Shared/StopNameAndType";
import {
  DirtyBadge,
  DirtyLabel,
  useElementStatusEnabled,
  useStopPlaceDirtyKeys,
} from "../../Shared/ElementStatus";
import { StopPlaceGeneralSectionProps } from "../types";
import {
  parseModalityPickerValue,
  toModalityPickerValue,
} from "../utils/modalityPickerValue";
import { StopPostalAddressFields } from "../../Shared/StopDetails/StopPostalAddressFields";
import { StopUrlField } from "../../Shared/StopDetails/StopUrlField";
import { StopPlaceCodesFields } from "./StopPlaceCodesFields";
import { generalSectionStyles as sx } from "./StopPlaceGeneralSection.styles";

/**
 * General info section: name, description, stop type, submode, tags, actions
 */
export const StopPlaceGeneralSection: React.FC<
  StopPlaceGeneralSectionProps
> = ({
  stopPlace,
  canEdit,
  onNameChange,
  onDescriptionChange,
  onModalityChange,
  onWeightingChange,
  version,
  onOpenVersions,
  onOpenTimetable,
  onOpenTags,
  onOpenAltNames,
}) => {
  const { formatMessage } = useIntl();
  const isStatusEnabled = useElementStatusEnabled();
  const dirtyKeys = useStopPlaceDirtyKeys();
  const isDirty = (...keys: string[]) =>
    isStatusEnabled && keys.some((key) => dirtyKeys.has(key));
  /* Required-but-empty blocks saving, so it reads as an error rather than a hint. */
  const isNameMissing = !stopPlace.name?.trim();
  const hiddenStopTypes = useHiddenStopTypes();
  /* "other" and hidden types count as missing too: they can't be saved (F-15). */
  const isStopPlaceTypeMissing = !isStopPlaceTypeValid(
    stopPlace.stopPlaceType,
    hiddenStopTypes,
  );

  const pickerValue = toModalityPickerValue(
    stopPlace.stopPlaceType,
    stopPlace.submode,
  );

  const handlePickerChange = (value: string) => {
    const { stopPlaceType, submode } = parseModalityPickerValue(value);
    // Single atomic change: the reducer sets stopPlaceType + submode together,
    // so dispatching type and submode separately would let the second overwrite
    // the first with a stale type.
    onModalityChange(stopPlaceType, submode);
  };

  // Types and submodes outside the user's permissions are shown but
  // disabled, as in legacy (F-18); the current value always stays selectable.
  const allowance = getModalityAllowance((stopPlace as any).permissions);
  const isOptionAllowed = (stopPlaceType: string, submode: string | null) =>
    isModalityAllowed(allowance, stopPlaceType, submode);

  const unifiedOptions = Object.entries(stopTypes)
    .filter(([key]) => !hiddenStopTypes.includes(key))
    .flatMap(([key, config]) => {
      const typeLabel = formatMessage({ id: `stopTypes_${key}_name` });
      const submodes = (config as any).submodes as string[] | null | undefined;
      const bare = {
        value: toModalityPickerValue(key, null),
        label: typeLabel,
        allowed: isOptionAllowed(key, null),
      };
      const submodeItems = (submodes ?? []).map((sub) => ({
        value: toModalityPickerValue(key, sub),
        label: formatMessage({ id: `stopTypes_${key}_submodes_${sub}` }),
        allowed: isOptionAllowed(key, sub),
      }));
      return [bare, ...submodeItems];
    });

  return (
    <Box sx={sx.container}>
      {/* Parent / group memberships — layout chosen under Utseende */}
      <StopPlaceMembership
        placement="inline"
        parentStop={
          stopPlace.isChildOfParent ? stopPlace.parentStop : undefined
        }
        groups={stopPlace.groups}
        currentName={stopPlace.name}
      />

      {/* Name */}
      <Box sx={sx.fieldRow}>
        <TextField
          label={
            <DirtyLabel dirty={isDirty("name")}>
              {`${formatMessage({ id: "name" })} *`}
            </DirtyLabel>
          }
          value={stopPlace.name || ""}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={!canEdit}
          error={isNameMissing}
          helperText={
            isNameMissing
              ? formatMessage({ id: "name_is_required" })
              : undefined
          }
          fullWidth
          size="small"
          variant="outlined"
          slotProps={{ htmlInput: { "aria-required": true } }}
        />
      </Box>

      {/* Stop-level codes, when turned on in the settings */}
      <Box sx={sx.fieldRow}>
        <StopPlaceCodesFields
          publicCode={stopPlace.publicCode}
          privateCode={stopPlace.privateCode}
          canEdit={canEdit}
        />
      </Box>

      {/* Description */}
      <Box sx={sx.fieldRow}>
        <TextField
          label={
            <DirtyLabel dirty={isDirty("description")}>
              {formatMessage({ id: "description" })}
            </DirtyLabel>
          }
          value={stopPlace.description || ""}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={!canEdit}
          fullWidth
          size="small"
          variant="outlined"
          multiline
          rows={2}
        />
      </Box>

      {/* URL and postal address, behind their feature flags (legacy parity) */}
      <Box sx={sx.fieldRow}>
        <StopUrlField url={stopPlace.url} canEdit={canEdit} />
      </Box>
      <Box sx={sx.fieldRow}>
        <StopPostalAddressFields canEdit={canEdit} />
      </Box>

      {/* Unified stop type + submode picker */}
      <Box
        sx={{ ...sx.fieldRow, display: "flex", alignItems: "center", gap: 1 }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ModalityIconImg
            type={stopPlace.stopPlaceType || "other"}
            submode={stopPlace.submode}
            svgStyle={{ width: 28, height: 28 }}
          />
        </Box>
        <FormControl
          size="small"
          disabled={!canEdit}
          error={isStopPlaceTypeMissing}
          fullWidth
        >
          <InputLabel>
            <DirtyLabel dirty={isDirty("stopPlaceType", "submode")}>
              {`${formatMessage({ id: "stopPlaceType" })} *`}
            </DirtyLabel>
          </InputLabel>
          <Select
            value={pickerValue}
            label={
              <DirtyLabel dirty={isDirty("stopPlaceType", "submode")}>
                {`${formatMessage({ id: "stopPlaceType" })} *`}
              </DirtyLabel>
            }
            onChange={(e) => handlePickerChange(e.target.value)}
          >
            {unifiedOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={!option.allowed && option.value !== pickerValue}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {isStopPlaceTypeMissing && (
            <FormHelperText>
              {formatMessage({ id: "field_is_required" })}
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Interchange weighting */}
      <Box sx={sx.fieldRow}>
        <FormControl size="small" disabled={!canEdit} fullWidth>
          <InputLabel>
            <DirtyLabel dirty={isDirty("weighting")}>
              {formatMessage({ id: "interchange_weighting" })}
            </DirtyLabel>
          </InputLabel>
          <Select
            value={stopPlace.weighting || ""}
            label={
              <DirtyLabel dirty={isDirty("weighting")}>
                {formatMessage({ id: "interchange_weighting" })}
              </DirtyLabel>
            }
            onChange={(e) => onWeightingChange(e.target.value)}
          >
            <MenuItem value="">
              <em>{formatMessage({ id: "weightTypes_noValue" })}</em>
            </MenuItem>
            {weightTypes.map((wt) => (
              <MenuItem key={wt} value={wt}>
                {formatMessage({ id: `weightTypes_${wt}` })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tariff zones (read-only) */}
      <Box sx={{ ...sx.fieldRow, display: "flex", gap: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 80, pt: 0.25 }}
        >
          {formatMessage({ id: "tariffZones" })}
        </Typography>
        <Typography
          variant="body2"
          color={
            (stopPlace.tariffZones ?? []).length > 0
              ? "text.primary"
              : "text.disabled"
          }
        >
          {(stopPlace.tariffZones ?? []).length > 0
            ? stopPlace
                .tariffZones!.map((tz) => tz.name?.value ?? tz.id)
                .join(", ")
            : formatMessage({ id: "not_present" })}
        </Typography>
      </Box>

      {/* Tags tray (read-only display) */}
      {stopPlace.tags && stopPlace.tags.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <TagTray tags={stopPlace.tags} />
        </Box>
      )}

      {/* Tags + Alt Names + Key Values + Versions — all in one row */}
      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        {canEdit && (
          <Button
            size="small"
            /* No dirty dot: tags are a separate Tiamat entity attached by
               idReference and mutated immediately, so they are never part of the
               stop place's unsaved state. See stopPlaceFieldStatus.ts. */
            startIcon={<LabelIcon fontSize="small" />}
            onClick={onOpenTags}
            variant="outlined"
          >
            {formatMessage({ id: "tags" })}
          </Button>
        )}
        <Button
          size="small"
          startIcon={
            <DirtyBadge dirty={isDirty("alternativeNames")}>
              <ShortTextIcon fontSize="small" />
            </DirtyBadge>
          }
          onClick={onOpenAltNames}
          variant="outlined"
        >
          {formatMessage({ id: "alternative_names" })}
        </Button>
        {version !== undefined &&
          version !== null &&
          !stopPlace.isChildOfParent && (
            <Button
              size="small"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={onOpenVersions}
              variant="outlined"
            >
              {formatMessage({ id: "version" })} {version}
            </Button>
          )}
        {onOpenTimetable && (
          <Button
            size="small"
            startIcon={<CalendarMonthIcon fontSize="small" />}
            onClick={onOpenTimetable}
            variant="outlined"
          >
            {formatMessage({ id: "timetable" })}
          </Button>
        )}
      </Box>
    </Box>
  );
};
